import React, { useState, useEffect } from "react";
import {
  ArrowDownToLine,
  Printer,
  ChevronLeft,
  Settings,
  Calendar,
  FileText,
  FileSpreadsheet as FileIcon,
} from "lucide-react";
import LedgerTable, {
  defaultColumns,
} from "./Components/LedgerComponents/LedgerTable";
import LedgerHeader from "./Components/LedgerComponents/LedgerHeader";
import LedgerSummary from "./Components/LedgerComponents/LedgerSummary";
import { dummyLedgerData } from "../../../dummyData";
import { Permissions } from "../../../permissions";
import { NavLink } from "react-router-dom";
import {
  Menu,
  MenuItem,
  Tooltip,
  IconButton,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";

const LedgerDetailsScreen = () => {
  const [customerData] = useState(dummyLedgerData);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(
    defaultColumns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}),
  );

  // Initialize date range from customer data
  useEffect(() => {
    if (customerData?.customerInfo?.period) {
      const { from, to } = customerData.customerInfo.period;
      setDateRange({ from, to });
    }
  }, [customerData]);

  const handleExportMenuOpen = event => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportAnchorEl(null);
  };

  const handleSettingsClick = event => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleDateRangeOpen = () => {
    setDateRangeOpen(true);
    // Initialize the date pickers with current range values
    if (dateRange) {
      const fromParts = dateRange.from.split("/");
      const toParts = dateRange.to.split("/");
      setFromDate(new Date(fromParts[2], fromParts[1] - 1, fromParts[0]));
      setToDate(new Date(toParts[2], toParts[1] - 1, toParts[0]));
    }
  };

  const handleDateRangeClose = () => {
    setDateRangeOpen(false);
  };

  const handleApplyDateRange = () => {
    if (fromDate && toDate) {
      const formattedFrom = format(fromDate, "dd/MM/yyyy");
      const formattedTo = format(toDate, "dd/MM/yyyy");
      setDateRange({ from: formattedFrom, to: formattedTo });
    }
    setDateRangeOpen(false);
  };

  const handleClearDateRange = () => {
    setFromDate(null);
    setToDate(null);
    setDateRange(null);
    setDateRangeOpen(false);
  };

  const handleColumnToggle = columnId => {
    const column = defaultColumns.find(col => col.id === columnId);
    if (column?.required) return;

    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  return (
    <>
      <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
        <div className="flex items-center">
          <NavLink
            to="/report/ledger"
            className="mr-4 flex items-center text-gray-600 hover:text-[#5652B0] transition-colors">
            <ChevronLeft size={20} />
          </NavLink>
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Ledger Report
          </h1>
        </div>

        <div className="flex items-center space-x-3">
          <Tooltip title="Change Date Range">
            <IconButton
              onClick={handleDateRangeOpen}
              className="border border-gray-200 bg-white hover:bg-gray-50"
              size="small">
              <Calendar size={18} className="text-gray-700" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Column Settings">
            <IconButton
              onClick={handleSettingsClick}
              className="border border-gray-200 bg-white hover:bg-gray-50"
              size="small">
              <Settings size={18} className="text-gray-700" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Export Options">
            <span>
              <Button
                className="px-4 py-2 rounded text-white bg-gray-400 cursor-not-allowed transition-all text-sm flex items-center"
                disabled={true}
                variant="contained"
                sx={{
                  backgroundColor: "#9CA3AF",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: "500",
                }}>
                <ArrowDownToLine size={16} className="mr-2" />
                Export
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className="p-6 bg-gray-50 min-h-[calc(100vh-130px)]">
        {/* Customer Info */}
        <LedgerHeader customerInfo={customerData.customerInfo} />

        {/* Transaction Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-700">
              Transaction Details
            </h3>
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-4">
                {customerData.transactions.length} transactions
              </span>
              {dateRange && (
                <span
                  className="text-xs px-2.5 py-1 bg-purple-50 text-purple-800 rounded-full font-medium cursor-pointer"
                  onClick={handleDateRangeOpen}>
                  {dateRange.from} - {dateRange.to}
                </span>
              )}
            </div>
          </div>
          <LedgerTable
            dateRange={dateRange}
            onSettingsClick={handleSettingsClick}
            anchorEl={settingsAnchorEl}
            onCloseSettings={handleSettingsClose}
            visibleColumns={visibleColumns}
            onColumnToggle={handleColumnToggle}
          />
        </div>

        {/* Summary */}
        <LedgerSummary summary={customerData.summary} />

        {/* Date Range Dialog */}
        <Dialog
          open={dateRangeOpen}
          onClose={handleDateRangeClose}
          PaperProps={{
            sx: {
              width: "400px",
              maxWidth: "90vw",
              borderRadius: "8px",
            },
          }}>
          <DialogTitle
            sx={{ borderBottom: "1px solid #E5E7EB", padding: "16px 24px" }}>
            <div className="flex items-center">
              <Calendar size={18} className="text-[#5652B0] mr-2" />
              <span className="text-lg font-medium">Select Date Range</span>
            </div>
          </DialogTitle>
          <DialogContent sx={{ padding: "24px" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <div className="space-y-4">
                <div>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    onChange={newValue => setFromDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#5652B0",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#5652B0",
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    onChange={newValue => setToDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#5652B0",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#5652B0",
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions
            sx={{ padding: "16px 24px", borderTop: "1px solid #E5E7EB" }}>
            <Button
              onClick={handleClearDateRange}
              sx={{
                color: "#5652B0",
                "&:hover": {
                  backgroundColor: "rgba(86, 82, 176, 0.04)",
                },
                textTransform: "none",
                fontWeight: "500",
              }}>
              Clear
            </Button>
            <Button
              onClick={handleDateRangeClose}
              sx={{
                color: "#5652B0",
                "&:hover": {
                  backgroundColor: "rgba(86, 82, 176, 0.04)",
                },
                textTransform: "none",
                fontWeight: "500",
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleApplyDateRange}
              variant="contained"
              disabled={!fromDate || !toDate}
              sx={{
                backgroundColor: "#5652B0",
                "&:hover": {
                  backgroundColor: "#4A47A3",
                },
                textTransform: "none",
                fontWeight: "500",
              }}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default LedgerDetailsScreen;
